import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import Heading from '../../generic-components/Heading';
import { useStore } from '../../store';
import { WidgetWrapper } from './components/WidgetWrapper';
import { CheckingAccountWidget } from './widgets/CheckingAccountWidget/CheckingAccountWidget';
import { SavingAccountWidget } from './widgets/SavingAccountWidget/SavingAccountWidget';
import { SavingsPromotionWidget } from './widgets/SavingsPromotionWidget/SavingsPromotionWidget';
import { validateFeatureFlag } from '../../services/featureFlagService';
import { FEATURE_FLAG } from '../../constants/general';
import { LatestTransactionsWidget } from './widgets/LatestTransactionsWidget/LatestTransactionsWidget';
import { DashboardContext } from './contexts/DashboardContext';
import { ExpensesInsight } from './widgets/ExpensesInsight';
import { useNewDashboardDataQuery } from '../../hooks/query/useNewDashboardDataQuery';
import { InvoicesWidget } from './widgets/InvoicesWidget/InvoicesWidget';
import { BillsWidget } from './widgets/BillsWidget/BillsWidget';
import { showBillsInvoicesGusto } from './consts';
import { IncomesInsight } from './widgets/IncomesInsights';
import { trackMixPanel } from '../../services/mixpanel.service';
import { GustoWidgetV2 } from './widgets/GustoWidgetV2';
import { useGetGustoDataForWidget } from '../../hooks/query/useGetGustoDataForWidget';
import { shouldShowGustoWidget } from './utils/widget.utils';
import { ProfitInsights } from './widgets/ProfitInsights';
import { UploadDocFlow } from '../../flows/uploadDoc/UploadDocFlow';
import { useGetMissingDoc } from '../../flows/uploadDoc/hooks/useGetMissingDoc';
import { SupportedSearchParams, useGetInitialSearchParam } from '../../hooks/utilities/useGetInitialSearchParam';
import {
  DashboardNotificationManager,
  DashboardNotificationType,
} from './components/banner/DashboardNotificationManager';
import { CategorizationTinder } from './widgets/CategorizationWidget/CategorizationTinder';
import { CategorizationWidget } from './widgets/CategorizationWidget/CategorizationWidget';
import { featureAccess, LimitedFeatureType } from '../../services/appAccess.service';
import { FeatureAccess } from '../AccountSettings/components/rolesAndPermissions/PermissionsList';

export const DashboardWrapper = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { customerId } = useStore((state) => state.currUser());
  const { getPrimaryAccount } = useStore((state) => state);
  const userData = useStore((state) => state.getSummaryData());
  const actionFlags = useStore((state) => state.summary.actionFlags);
  const setTaxSaving = useStore((state) => state.setTaxSavingAmount);
  const redirectTo = useStore((state) => state.getRedirection());
  const setRedirection = useStore((state) => state.setRedirection);
  const summary = useStore((state) => state.summary);
  const { payrollCompanyId } = summary;
  const [shouldShowUploadDocFlow, setShouldShowUploadDocFlow] = useState<boolean>(false);
  const [notifications, setNotifications] = useState<DashboardNotificationType[]>([]);

  const { queryValue } = useGetInitialSearchParam({
    queryKey: SupportedSearchParams.OM,
    shouldClearParamAfterGet: true,
  });

  const {
    data,
    isLoading,
    refetch: refetchDashboardData,
  } = useNewDashboardDataQuery({
    customerId,
    transactionsAmount: 3,
  });
  const { refetch: refetchMissingDocs } = useGetMissingDoc({
    businessId: userData.business.externalId,
    businessType: userData.business.type,
    onError: () => {},
  });

  const {
    refetch: getGustoData,
    data: gustoData,
    isLoading: isLoadingGusto,
    isRefetching: isRefetchingGusto,
  } = useGetGustoDataForWidget({
    customerId,
    companyId: payrollCompanyId?.[0] || '',
  });
  const dashboardContextValue = useMemo(
    () => ({
      latestTransactions: data?.latestTransactions?.transactionList,
      uncategorizedTransactions: data?.uncategorizedTransactions || {
        expenseTransactionsList: [],
        incomeTransactionsList: [],
      },
      taxSaving: data?.taxRequestResponse?.taxEstimate?.taxSaving,
      lastInvoices: data?.lastInvoices,
      lastBills: data?.lastBills,
      totalBillsDueAmount: data?.totalBillsDueAmount,
      totalInvoicedAmount: data?.totalInvoicedAmount,
      totalOutstandingInvoiceAmount: data?.totalOutstandingInvoiceAmount,
      profitChartData: data?.profitChartData,
      expensesChartData: data?.expensesChartData,
      incomeChartData: data?.incomeChart,
      chartFilters: data?.chartFilter,
    }),
    [data],
  );
  const showSavingAccountWidget = validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT);
  const showNewGustoWidget = shouldShowGustoWidget(gustoData);
  const showDashboardCharts = validateFeatureFlag(FEATURE_FLAG.CHARTS);
  const showIncomeChart = validateFeatureFlag(FEATURE_FLAG.INCOME_CHART);
  const showSegmentedIncomeChart = validateFeatureFlag(FEATURE_FLAG.SEGMENTED_INCOME_CHART);
  const addNotification = (type: DashboardNotificationType) => {
    setNotifications([...notifications, type]);
  };
  const removeNotification = (type: DashboardNotificationType) => {
    setNotifications(notifications?.filter((notification) => type !== notification));
  };
  const checkIfeinIsNeeded = (triggerFlow: boolean) => {
    if (userData.business.status === 'PENDING') {
      refetchMissingDocs().then((response) => {
        if (response) {
          if (triggerFlow) {
            setShouldShowUploadDocFlow(true);
          } else {
            const canUpdateBusinessDetails =
              featureAccess(null, LimitedFeatureType.BusinessDetails) === FeatureAccess.READ_WRITE;
            if (canUpdateBusinessDetails) {
              addNotification(DashboardNotificationType.UploadEIN);
            }
          }
        }
      });
    }
  };

  useEffect(() => {
    if (queryValue === 'upload_docs') {
      checkIfeinIsNeeded(true);
    }
  }, [queryValue]);

  useEffect(() => {
    if (data?.taxRequestResponse?.taxEstimate) {
      setTaxSaving(data?.taxRequestResponse?.taxEstimate?.taxSaving);
    }
  }, [data?.taxRequestResponse]);

  useEffect(() => {
    (async () => {
      await getGustoData();
    })();
    if (redirectTo) {
      navigate(redirectTo);
      setRedirection('');
    }
    trackMixPanel('View Dashboard', {
      origin: window.location.pathname === '/dashboard' ? 'Click dashboard on menu' : 'Login',
    });
    const canFundAccount =
      featureAccess(null, LimitedFeatureType.AddMoneyACHLinkedAccount) === FeatureAccess.READ_WRITE;
    if (!actionFlags.accountFunded && canFundAccount) {
      addNotification(DashboardNotificationType.AddMoney);
    }
  }, []);
  useEffect(() => {
    refetchDashboardData();
    checkIfeinIsNeeded(false);
  }, [customerId]);

  useEffect(() => {
    if (payrollCompanyId) getGustoData();
  }, [payrollCompanyId]);
  const onNotificationClose = (type: DashboardNotificationType) => removeNotification(type);
  const onNotificationClick = (type: DashboardNotificationType) => {
    switch (type) {
      case DashboardNotificationType.UploadEIN:
        setShouldShowUploadDocFlow(true);
        return;
      default:
        break;
    }
    removeNotification(type);
  };

  const isHaveTransactions = data?.latestTransactions?.transactionList?.length > 0;
  return (
    <DashboardContext.Provider value={dashboardContextValue}>
      <Layout wrapperClassName='relative w-full h-full flex items-center justify-between bg-bw02 ml-[100px] pc-md:ml-[256px]'>
        <div className='w-full flex flex-col relative pt-[78px] pl-[72px] bg-bw02'>
          <DashboardNotificationManager
            notifications={notifications}
            onClose={onNotificationClose}
            onClick={onNotificationClick}
          />
          <div className='flex flex-col h-full mx-auto mb-[95px] new-tab-landscape:w-[1106px] new-pc-lg:w-[1300px] transition-width duration-500 ease-in-out'>
            <Heading
              title={t('DASHBOARD_FACELIFT.TITLE')}
              className='flex self-start flex-nowrap mb-[50px] font-metricRMedium text-bw08 text-[32px]'
            />
            <div className='flex flex-col h-full mb-[95px] new-tab-landscape:w-[1080px] new-pc-lg:w-[1300px] transition-width duration-500 ease-in-out'>
              <div className='w-full grid grid-cols-3 gap-[28px]'>
                <WidgetWrapper className='h-[188px]' testId='DashboardWidget_checking-account'>
                  <CheckingAccountWidget isLoading={isLoading} />
                </WidgetWrapper>
                <WidgetWrapper className='h-[188px]' testId='DashboardWidget_savings-account'>
                  {showSavingAccountWidget && <SavingAccountWidget isLoading={isLoading} />}
                  {!showSavingAccountWidget && <SavingsPromotionWidget isLoading={isLoading} />}
                </WidgetWrapper>

                {!isHaveTransactions ? (
                  <div className='h-[537px] row-span-2 col-span-1 w-full'>
                    <CategorizationTinder isLoading={isLoading} />
                  </div>
                ) : (
                  <WidgetWrapper
                    className='h-[537px] row-span-2 col-span-1'
                    testId='DashboardWidget_latest-transactions'
                  >
                    <CategorizationWidget isLoading={isLoading} />
                  </WidgetWrapper>
                )}

                <WidgetWrapper className='h-[322px] col-span-2' testId='DashboardWidget_latest-transactions'>
                  <LatestTransactionsWidget isLoading={isLoading} />
                </WidgetWrapper>
              </div>
              {showDashboardCharts && (
                <div className='w-full grid grid-cols-3 gap-[28px] mt-[28px]'>
                  <WidgetWrapper className='min-h-[180p]' testId='insights_widgets'>
                    <ProfitInsights isLoading={isLoading} />
                  </WidgetWrapper>
                  <WidgetWrapper className='min-h-[180px]' testId='insights_widgets'>
                    <ExpensesInsight isLoading={isLoading} />
                  </WidgetWrapper>

                  {(showIncomeChart || showSegmentedIncomeChart) && (
                    <WidgetWrapper className='min-h-[180px]' testId='insights_widgets'>
                      <IncomesInsight isLoading={isLoading} showHorizontalChart={showIncomeChart} />
                    </WidgetWrapper>
                  )}
                </div>
              )}
              {showBillsInvoicesGusto && (
                <>
                  <div className='w-full grid grid-cols-3 gap-[28px] mt-[28px]'>
                    <WidgetWrapper className='h-[356px] col-span-2' testId='DashboardWidget_invoices'>
                      <InvoicesWidget isLoading={isLoading} />
                    </WidgetWrapper>
                    {showNewGustoWidget && gustoData && (
                      <WidgetWrapper className='h-[356px] col-span-1' testId='DashboardWidget_gusto'>
                        <GustoWidgetV2
                          isLoading={isLoadingGusto || isRefetchingGusto}
                          gustoData={gustoData}
                          balance={getPrimaryAccount().balance}
                        />
                      </WidgetWrapper>
                    )}
                  </div>
                  <div className='w-full grid grid-cols-3 gap-[28px] mt-[28px]'>
                    <WidgetWrapper className='h-[356px] col-span-2' testId='DashboardWidget_bills'>
                      <BillsWidget isLoading={isLoading} />
                    </WidgetWrapper>
                  </div>
                </>
              )}
            </div>
          </div>
          {shouldShowUploadDocFlow && (
            <UploadDocFlow
              onClose={() => setShouldShowUploadDocFlow(false)}
              onSuccess={() => removeNotification(DashboardNotificationType.UploadEIN)}
            />
          )}
        </div>
      </Layout>
    </DashboardContext.Provider>
  );
};
